import { BillingCycle, IProductEntity } from '@/features/common/pricing';
import type {
  IProductMetadata,
  ProductCredits,
  ProductMetadataFeature,
  ProductPrice,
} from '../entities/ProductMetadata';

export const PRODUCT_METADATA_FEATURES_BY_ORDER: ProductMetadataFeature[] = [
  'bulk_credits',
  'chrome_extension',
  'api_access',
  'team_member_management',
  'csv_enrichment',
  'data_export',
  'bulk_save',
  'crm_integration',
  'list_management',
];

/**
 * Provides general algorithm for building product metadata,
 * which have to be used by concrete builders for concrete plan.
 */
export abstract class ProductMetadataBuilder {
  constructor(
    protected products: IProductEntity[],
    protected billingCycle: BillingCycle,
  ) {}

  protected getMonthlyProduct(): IProductEntity {
    const product = this.products.find((product) => product.cycle === 'monthly');
    if (!product) throw new Error('Monthly product not found');
    return product;
  }

  protected getAnnualProduct(): IProductEntity {
    const product = this.products.find((product) => product.cycle === 'annually');
    if (!product) throw new Error('Annual product not found');
    return product;
  }

  protected getProduct(): IProductEntity {
    return this.billingCycle === BillingCycle.Monthly
      ? this.getMonthlyProduct()
      : this.getAnnualProduct();
  }

  protected getCreditsCount(
    product: IProductEntity,
    creditType: 'full' | 'bulk',
  ): ProductCredits {
    const quota = product.quotas.find((quota) => quota.creditType === creditType);
    if (!quota) return 0;
    if (quota.isUnlimited) return 'unlimited';
    return (quota.creditsFixed ?? 0) + (quota.creditsPerSeat ?? 0);
  }

  protected getPrice(
    product: IProductEntity,
    productToCompare?: IProductEntity,
  ): ProductPrice {
    const price = product.cycle === 'annually' ? product.price / 12 : product.price;

    if (!productToCompare) return { price: Math.round(price) };

    return {
      price: Math.round(price),
    };
  }

  protected getTotalPrice(product: IProductEntity): number {
    if (product.price === 0) return 0;

    if (product.isPriceFixed) return product.price;

    return product.price;
  }

  protected getId(product: IProductEntity): string {
    return product.id;
  }

  protected getName(): string {
    const product = this.products.find((p) => {
      if (this.billingCycle === BillingCycle.Monthly) {
        return p.cycle === 'monthly';
      }
      return p.cycle === 'annually';
    });

    return product?.name ?? '';
  }

  protected applyFeaturesOrderComparator(
    features: ProductMetadataFeature[],
  ): ProductMetadataFeature[] {
    return PRODUCT_METADATA_FEATURES_BY_ORDER.filter((feature) =>
      features.includes(feature),
    );
  }

  protected getFeatures(product: IProductEntity): ProductMetadataFeature[] {
    const features = new Set([...product.features, ...this.getVirtualFeatures(product)]);

    if (features.has('bulk_save') || features.has('csv_enrichment')) {
      features.add('bulk_credits');
    }

    return PRODUCT_METADATA_FEATURES_BY_ORDER.filter((feature) => features.has(feature));
  }

  /**
   * Every product meta builder should returl own virtual features
   * that are not available in the product features
   */
  abstract getVirtualFeatures(product: IProductEntity): ProductMetadataFeature[];

  abstract getIsMostPopular(): boolean;

  protected getAnnualDiscount(): number {
    const monthlyProduct = this.getMonthlyProduct();
    const annualProduct = this.getAnnualProduct();

    if (monthlyProduct.isPriceFixed !== annualProduct.isPriceFixed) return 0;

    const monthlyPrice = monthlyProduct.price;
    const annualPrice = annualProduct.price / 12;

    return Math.max(0, Math.round(((monthlyPrice - annualPrice) / monthlyPrice) * 100));
  }

  build(): IProductMetadata {
    const monthlyProduct = this.getMonthlyProduct();
    const annualProduct = this.getAnnualProduct();

    return {
      name: this.getName(),
      family: monthlyProduct.family,
      mostPopular: this.getIsMostPopular(),
      isCustom: false,
      annualDiscount: this.getAnnualDiscount(),
      variants: {
        [BillingCycle.Monthly]: {
          id: this.getId(monthlyProduct),
          name: this.getName(),
          price: this.getPrice(monthlyProduct),
          totalPrice: this.getTotalPrice(monthlyProduct),
          isPriceFixed: monthlyProduct.isPriceFixed,
          fullCredits: this.getCreditsCount(monthlyProduct, 'full'),
          bulkCredits: this.getCreditsCount(monthlyProduct, 'bulk'),
          features: this.getFeatures(monthlyProduct),
          product: monthlyProduct,
        },
        [BillingCycle.Yearly]: {
          id: this.getId(annualProduct),
          name: this.getName(),
          price: this.getPrice(annualProduct, monthlyProduct),
          totalPrice: this.getTotalPrice(annualProduct),
          isPriceFixed: annualProduct.isPriceFixed,
          fullCredits: this.getCreditsCount(annualProduct, 'full'),
          bulkCredits: this.getCreditsCount(annualProduct, 'bulk'),
          features: this.getFeatures(annualProduct),
          product: annualProduct,
        },
      },
    };
  }
}
